import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFileUploadLimits } from '../../../../hooks/useFileUploadLimits';
import Icon from '../../../../components/AppIcon';
import { DateField, TimeField } from '../../../../components/dateFields';
import { todayLocalDateParam } from '@/utils/apiDate';

const CorrectionForm = ({ onSubmit, initialValues }) => {
  const { t } = useTranslation();
  const { validateFileSize } = useFileUploadLimits();
  const [formData, setFormData] = useState({
    date: '',
    type: '',
    requestedCheckIn: '',
    requestedCheckOut: '',
    currentCheckIn: '',
    currentCheckOut: '',
    reason: '',
    attachments: []
  });

  useEffect(() => {
    if (!initialValues) return;
    setFormData((prev) => ({
      ...prev,
      ...initialValues,
    }));
  }, [initialValues]);

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const correctionTypes = [
    { value: 'missing-clockout', label: t('Missing Clock Out'), description: t('Add missing check-out time') },
    { value: 'incorrect-time', label: t('Incorrect Time'), description: t('Correct wrong clock-in/out times') },
    { value: 'missing-entry', label: t('Missing Entry'), description: t('Add completely missing attendance record') },
    { value: 'break-time', label: t('Break Time Correction'), description: t('Adjust break time records') }
  ];

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.date) {
      newErrors.date = t('Date is required');
    }

    if (!formData?.type) {
      newErrors.type = t('Correction type is required');
    }

    if (!formData?.reason?.trim()) {
      newErrors.reason = t('Reason is required');
    } else if (formData?.reason?.trim()?.length < 10) {
      newErrors.reason = t('Reason must be at least 10 characters long');
    }

    // Validate times based on correction type
    if (formData?.type === 'incorrect-time' || formData?.type === 'missing-entry') {
      if (!formData?.requestedCheckIn) {
        newErrors.requestedCheckIn = t('Check-in time is required for this correction type');
      }
    }

    if (formData?.type === 'missing-clockout' || formData?.type === 'incorrect-time' || formData?.type === 'missing-entry') {
      if (!formData?.requestedCheckOut) {
        newErrors.requestedCheckOut = t('Check-out time is required for this correction type');
      }
    }

    // Validate time logic
    if (formData?.requestedCheckIn && formData?.requestedCheckOut) {
      const checkIn = new Date(`2000-01-01 ${formData?.requestedCheckIn}`);
      const checkOut = new Date(`2000-01-01 ${formData?.requestedCheckOut}`);
      
      if (checkOut <= checkIn) {
        newErrors.requestedCheckOut = t('Check-out time must be after check-in time');
      }
    }

    // Validate date is not in the future
    const selectedDate = new Date(formData?.date);
    const today = new Date();
    today?.setHours(23, 59, 59, 999);

    if (selectedDate > today) {
      newErrors.date = t('Cannot create correction requests for future dates');
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const extractApiErrorMessage = (e, fallback) => {
    if (!e) return fallback;
    if (typeof e === 'string') return e;

    const body = e?.response?.data || e?.data || e;

    if (body?.errors?.length) {
      const first = body.errors[0];
      const desc = first?.description || first?.message;
      if (desc) return desc;
    }

    const msg = body?.message || e?.message;
    if (msg) return msg;

    const status = e?.status || e?.response?.status;
    const statusText = e?.statusText || e?.response?.statusText;
    if (status) return statusText ? `${status} ${statusText}` : String(status);

    return fallback;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleFileUpload = (event) => {
    const files = Array.from(event?.target?.files);
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf', 'text/plain'];

    const validFiles = files?.filter(file => {
      if (!allowedTypes?.includes(file?.type)) {
        alert(t('File type {{type}} is not allowed. Please use: JPG, PNG, GIF, PDF, or TXT files.', { type: file?.type, nsSeparator: false }));
        return false;
      }
      const { valid, maxMB } = validateFileSize(file, 'clockingPhotoMaxSizeMB');
      if (!valid) {
        alert(t('fileSizeExceedsLimit', { name: file?.name, maxMB }));
        return false;
      }
      return true;
    });

    setFormData(prev => ({
      ...prev,
      attachments: [...prev?.attachments, ...validFiles]
    }));
  };

  const removeAttachment = (index) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev?.attachments?.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      await onSubmit?.(formData);
      
      // Reset form
      setFormData({
        date: '',
        type: '',
        requestedCheckIn: '',
        requestedCheckOut: '',
        currentCheckIn: '',
        currentCheckOut: '',
        reason: '',
        attachments: []
      });

      alert(t('Correction request submitted successfully!'));
      
    } catch (error) {
      alert(extractApiErrorMessage(error, t('Failed to submit request.')));
    } finally {
      setIsSubmitting(false);
    }
  };

  const getSelectedType = () => {
    return correctionTypes?.find(type => type?.value === formData?.type);
  };

  return (
    <div className="bg-card border border-border rounded-xl">
      <div className="p-6 border-b border-border">
        <h3 className="text-lg font-semibold text-foreground flex items-center">
          <Icon name="FileEdit" size={18} className="mr-2" />
          {t('Correction Request Form')}
        </h3>
        <p className="text-sm text-muted-foreground mt-1">
          {t('Submit a request to correct your attendance record with supporting documentation')}
        </p>
      </div>
      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        {/* Date and Type Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              {t('Date')} <span className="text-red-500">*</span>
            </label>
            <DateField
              value={formData?.date}
              onChange={(v) => handleInputChange('date', v)}
              inputClassName={errors?.date ? 'border-red-500 bg-red-50/50' : undefined}
              max={todayLocalDateParam()}
            />
            {errors?.date && (
              <p className="text-red-500 text-sm mt-1 flex items-center">
                <Icon name="AlertCircle" size={14} className="mr-1" />
                {errors?.date}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              {t('Correction Type')} <span className="text-red-500">*</span>
            </label>
            <select
              value={formData?.type}
              onChange={(e) => handleInputChange('type', e?.target?.value)}
              className={`w-full px-4 py-3 border rounded-lg focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary transition-colors ${
                errors?.type ? 'border-red-500 bg-red-50/50' : 'border-border bg-background'
              }`}
            >
              <option value="">{t('Select correction type')}</option>
              {correctionTypes?.map(type => (
                <option key={type?.value} value={type?.value}>
                  {type?.label}
                </option>
              ))}
            </select>
            {errors?.type && (
              <p className="text-red-500 text-sm mt-1 flex items-center">
                <Icon name="AlertCircle" size={14} className="mr-1" />
                {errors?.type}
              </p>
            )}
            {getSelectedType() && (
              <p className="text-muted-foreground text-sm mt-1">
                {getSelectedType()?.description}
              </p>
            )}
          </div>
        </div>

        {/* Time Correction Section */}
        {formData?.type && (
          <div className="bg-muted/30 rounded-lg p-4 space-y-4">
            <h4 className="text-sm font-semibold text-foreground flex items-center">
              <Icon name="Clock" size={16} className="mr-2" />
              {t('Time Correction Details')}
            </h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Current Times */}
              <div className="space-y-4">
                <h5 className="text-sm font-medium text-muted-foreground">{t('Current Times')}</h5>
                
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    {t('Current Check In')}
                  </label>
                  <TimeField
                    value={formData?.currentCheckIn}
                    onChange={(v) => handleInputChange('currentCheckIn', v)}
                    placeholder={t('No current check-in')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    {t('Current Check Out')}
                  </label>
                  <TimeField
                    value={formData?.currentCheckOut}
                    onChange={(v) => handleInputChange('currentCheckOut', v)}
                    placeholder={t('No current check-out')}
                  />
                </div>
              </div>

              {/* Requested Times */}
              <div className="space-y-4">
                <h5 className="text-sm font-medium text-foreground">{t('Requested Times')}</h5>
                
                {(formData?.type === 'incorrect-time' || formData?.type === 'missing-entry') && (
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      {t('Requested Check In')} <span className="text-red-500">*</span>
                    </label>
                    <TimeField
                      value={formData?.requestedCheckIn}
                      onChange={(v) => handleInputChange('requestedCheckIn', v)}
                      inputClassName={errors?.requestedCheckIn ? 'border-red-500 bg-red-50/50' : undefined}
                    />
                    {errors?.requestedCheckIn && (
                      <p className="text-red-500 text-sm mt-1 flex items-center">
                        <Icon name="AlertCircle" size={14} className="mr-1" />
                        {errors?.requestedCheckIn}
                      </p>
                    )}
                  </div>
                )}

                {(formData?.type === 'missing-clockout' || formData?.type === 'incorrect-time' || formData?.type === 'missing-entry') && (
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      {t('Requested Check Out')} <span className="text-red-500">*</span>
                    </label>
                    <TimeField
                      value={formData?.requestedCheckOut}
                      onChange={(v) => handleInputChange('requestedCheckOut', v)}
                      inputClassName={errors?.requestedCheckOut ? 'border-red-500 bg-red-50/50' : undefined}
                    />
                    {errors?.requestedCheckOut && (
                      <p className="text-red-500 text-sm mt-1 flex items-center">
                        <Icon name="AlertCircle" size={14} className="mr-1" />
                        {errors?.requestedCheckOut}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Reason Section */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            {t('Detailed Reason')} <span className="text-red-500">*</span>
          </label>
          <textarea
            value={formData?.reason}
            onChange={(e) => handleInputChange('reason', e?.target?.value)}
            rows={4}
            placeholder={t('Provide a detailed explanation for this correction request...')}
            className={`w-full px-4 py-3 border rounded-lg focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary transition-colors resize-none ${
              errors?.reason ? 'border-red-500 bg-red-50/50' : 'border-border bg-background'
            }`}
          />
          <div className="flex justify-between items-center mt-1">
            {errors?.reason && (
              <p className="text-red-500 text-sm flex items-center">
                <Icon name="AlertCircle" size={14} className="mr-1" />
                {errors?.reason}
              </p>
            )}
            <p className="text-muted-foreground text-sm ml-auto">
              {formData?.reason?.length || 0}/500 {t('characters')}
            </p>
          </div>
        </div>

        {/* Attachments Section */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            {t('Supporting Documents')}
          </label>
          <div className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-orange-300 transition-colors">
            <input
              type="file"
              multiple
              accept=".jpg,.jpeg,.png,.gif,.pdf,.txt"
              onChange={handleFileUpload}
              className="hidden"
              id="file-upload"
            />
            <label
              htmlFor="file-upload"
              className="cursor-pointer flex flex-col items-center space-y-2"
            >
              <Icon name="Upload" size={24} className="text-muted-foreground" />
              <div className="text-sm text-foreground">
                <span className="text-orange-600 font-medium">{t('Click to upload')}</span> {t('or drag and drop')}
              </div>
              <div className="text-xs text-muted-foreground">
                {t('JPG, PNG, GIF, PDF, TXT up to 5MB each')}
              </div>
            </label>
          </div>

          {/* Uploaded Files */}
          {formData?.attachments?.length > 0 && (
            <div className="mt-4 space-y-2">
              <p className="text-sm font-medium text-foreground">{t('Uploaded Files:', { nsSeparator: false })}</p>
              {formData?.attachments?.map((file, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Icon name="File" size={16} className="text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium text-foreground">{file?.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {(file?.size / 1024)?.toFixed(1)} KB
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeAttachment(index)}
                    className="text-red-500 hover:text-red-700 transition-colors"
                  >
                    <Icon name="X" size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Submit Button */}
        <div className="flex justify-end space-x-4 pt-6 border-t border-border">
          <button
            type="button"
            className="px-6 py-3 text-sm font-medium text-muted-foreground bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors"
            disabled={isSubmitting}
          >
            {t('Save as Draft')}
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-8 py-3 text-sm font-medium text-white bg-gradient-to-r from-orange-500 to-red-600 rounded-lg hover:from-orange-600 hover:to-red-700 transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {isSubmitting && <Icon name="Loader2" size={16} className="animate-spin" />}
            <span>{isSubmitting ? t('Submitting...') : t('Submit Request')}</span>
          </button>
        </div>
      </form>
    </div>
  );
};

export default CorrectionForm;